import type { Ref } from 'vue'

import { gql } from '@apollo/client/core'

import type { RaidId, RaidStageId } from '../../types'

/**
 * Raid-board drag-and-drop waits for the post-commit `boardChanged` snapshot.
 * The mutation result is only an acknowledgement; this slice deliberately
 * never re-queries `RaidBoard`, so every tab applies the exact same server
 * order and membership at the same event boundary.
 */

const RaidGroupReorderDocument = gql`
  mutation RaidGroupReorder(
    $guildId: Int!
    $raidId: Int!
    $groupId: Int!
    $orderedPlayerIds: [Int!]!
    $stageId: Int
  ) {
    raidGroupReorder(
      guildId: $guildId
      raidId: $raidId
      groupId: $groupId
      orderedPlayerIds: $orderedPlayerIds
      stageId: $stageId
    )
  }
`

const RaidGroupSetCollapsedDocument = gql`
  mutation RaidGroupSetCollapsed(
    $guildId: Int!
    $raidId: Int!
    $groupId: Int!
    $collapsed: Boolean!
  ) {
    raidGroupSetCollapsed(
      guildId: $guildId
      raidId: $raidId
      groupId: $groupId
      collapsed: $collapsed
    )
  }
`

const RaidPlayerMoveDocument = gql`
  mutation RaidPlayerMove(
    $guildId: Int!
    $raidId: Int!
    $raidPlayerId: Int!
    $targetGroupId: Int!
    $insertIndex: Int!
    $stageId: Int
  ) {
    raidPlayerMove(
      guildId: $guildId
      raidId: $raidId
      raidPlayerId: $raidPlayerId
      targetGroupId: $targetGroupId
      insertIndex: $insertIndex
      stageId: $stageId
    )
  }
`

const RaidRoomMoveDocument = gql`
  mutation RaidRoomMove($guildId: Int!, $raidId: Int!, $roomId: Int!, $targetIndex: Int!) {
    raidRoomMove(guildId: $guildId, raidId: $raidId, roomId: $roomId, targetIndex: $targetIndex)
  }
`

export interface RaidGroupReorderMutation {
  readonly raidGroupReorder: boolean
}

export interface RaidGroupSetCollapsedMutation {
  readonly raidGroupSetCollapsed: boolean
}

export interface RaidPlayerMoveMutation {
  readonly raidPlayerMove: boolean
}

export interface RaidRoomMoveMutation {
  readonly raidRoomMove: boolean
}

type ReportingRunner = <T>(
  mutation: unknown,
  variables: Record<string, unknown>,
  acknowledge?: (result: T) => boolean,
) => Promise<boolean>

export interface RaidMovesCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly activeRaidId: Ref<RaidId | null>
  readonly activeStageId: Ref<RaidStageId | null>
  readonly runReporting: ReportingRunner
  /**
   * Reactive stable-id → DB-PK lookups derived from `raid.raidRooms` /
   * `raid.raidGroups` by the slim composer. The renderer hands stable ids
   * (`"group-5"`, `"pool"`, `"room-25"`) to these mutations, but the GraphQL
   * `Int!` arguments need the internal i64 PKs (`raid_groups.id`,
   * `raid_rooms.id`). Falling back to `Number(...)` keeps the old contract
   * working when a caller passes a raw numeric string. Snapshot player ids
   * are already numeric and do not use these stable-id lookups.
   *
   * These MUST be `Ref`s (not a plain `Map` snapshot): the maps are empty
   * until the initial raid-board snapshot resolves, and rebuild on every
   * subscription delivery. A one-time `.value` capture at store-construction time would
   * freeze the empty map and every stable-id lookup would fall through to
   * `Number("group-5")` → `NaN` → "Invalid value for argument groupId,
   * expected type Int". Reading `.value` at mutation time picks up the
   * latest snapshot.
   */
  readonly raidNumericGroupId: Ref<ReadonlyMap<string, number>>
  readonly raidNumericRoomId: Ref<ReadonlyMap<string, number>>
}

export interface RaidMovesApi {
  /**
   * Persist a new player order within one snapshot group.
   * `playerIds` is the full ordered list of raid-player ids in the group
   * after the drag (mirrors the live-mode `reorderGroup` contract, which
   * expects the same-permutation caller-supplied ordering).
   */
  readonly reorderGroup: (
    groupId: string,
    orderedPlayerIds: ReadonlyArray<string>,
  ) => Promise<boolean>
  /** Persist the collapsed state for one snapshot group. */
  readonly setGroupCollapsed: (groupId: string, collapsed: boolean) => Promise<boolean>
  /**
   * Persist a single snapshot player's move into a target group at the
   * given index. Both the source and destination groups are renumbered
   * server-side inside a single txn (the raid-move resolver mirrors the
   * live-mode `f_player_move`).
   */
  readonly movePlayerToGroup: (
    playerId: string,
    targetGroupId: string,
    index: number,
  ) => Promise<boolean>
  /**
   * Reorder a room within the snapshot (up/down arrows on
   * `RoomHeader`). The server pins the Pool to the trailing position so
   * the UI invariant matches the live board.
   */
  readonly moveRoom: (roomId: string, targetIndex: number) => Promise<boolean>
}

export function useRaidMoves(ctx: RaidMovesCtx): RaidMovesApi {
  async function reorderGroup(
    groupId: string,
    orderedPlayerIds: ReadonlyArray<string>,
  ): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) return false
    const numericGroupId = ctx.raidNumericGroupId.value.get(groupId) ?? Number(groupId)
    return ctx.runReporting<RaidGroupReorderMutation>(RaidGroupReorderDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      groupId: numericGroupId,
      orderedPlayerIds: orderedPlayerIds.map(Number),
      stageId: ctx.activeStageId.value === null ? null : Number(ctx.activeStageId.value),
    }, (result) => result.raidGroupReorder)
  }

  async function movePlayerToGroup(
    playerId: string,
    targetGroupId: string,
    index: number,
  ): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) return false
    // `playerId` is the snapshot row's id from `raidPlayerToPlayer`
    // (`id: rp.id`), already numeric — do not look it up through the
    // stable-id map. Only the target group's id is a stable id.
    const numericTargetGroupId = ctx.raidNumericGroupId.value.get(targetGroupId) ?? Number(targetGroupId)
    return ctx.runReporting<RaidPlayerMoveMutation>(RaidPlayerMoveDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      raidPlayerId: Number(playerId),
      targetGroupId: numericTargetGroupId,
      insertIndex: index,
      stageId: ctx.activeStageId.value === null ? null : Number(ctx.activeStageId.value),
    }, (result) => result.raidPlayerMove)
  }

  async function setGroupCollapsed(groupId: string, collapsed: boolean): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) return false
    const numericGroupId = ctx.raidNumericGroupId.value.get(groupId) ?? Number(groupId)
    return ctx.runReporting<RaidGroupSetCollapsedMutation>(RaidGroupSetCollapsedDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      groupId: numericGroupId,
      collapsed,
    }, (result) => result.raidGroupSetCollapsed)
  }

  async function moveRoom(roomId: string, targetIndex: number): Promise<boolean> {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) return false
    const numericRoomId = ctx.raidNumericRoomId.value.get(roomId) ?? Number(roomId)
    return ctx.runReporting<RaidRoomMoveMutation>(RaidRoomMoveDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      raidId: Number(raidId),
      roomId: numericRoomId,
      targetIndex,
    }, (result) => result.raidRoomMove)
  }

  return { reorderGroup, setGroupCollapsed, movePlayerToGroup, moveRoom }
}
